/**
 * @file Utils for error handling
 *
 * @see https://leaflink.atlassian.net/wiki/spaces/frontend/pages/2586345559/Frontend+Error+Handling
 *
 * Please update the above document as needed whenever the utils are changed.
 *
 * Also, please update /frontend/legacy/js/utils/error.js whenever this file is updated.
 */

import { AxiosError } from 'axios';
import isEmpty from 'lodash-es/isEmpty';
import isObject from 'lodash-es/isObject';
import isPlainObject from 'lodash-es/isPlainObject';
import { App } from 'vue';

import { URLS } from '@/constants';
import { env } from '@/constants/env';
import { APIError } from '@/models';
import { JsonError, JsonErrorDocument } from '@/models/jsonApi';
import notify from '@/notify';
import i18n from '@/plugins/i18n';
import { OmsServiceError } from '@/services/oms/errors';

const CUSTOMER_SUPPORT_MESSAGE = i18n.t('notify.errorCustomerSupport');

/**
 * A general error handling function that displays the error message and re-throws the error.
 * @param {unknown} error - should be an Error object
 * @param {string} customMessage - A context-specific message for the error
 * @throws the given error
 */
export function handleError(error?: unknown, customMessage?: string): never {
  notifyError(error, customMessage);

  if (error && typeof error === 'string') {
    throw new Error(error);
  } else {
    throw error;
  }
}

/**
 * A general error handling function that displays the error message.
 *
 * Notifying is skipped if the error has truthy values for any of the following properties:
 *    - preventNotify
 *    - wasNotified
 *
 * @param {unknown} error - should be an Error object
 * @param {string} customMessage - A context-specific message for the error
 */
export function notifyError(error?: unknown, customMessage?: string) {
  const err = error as APIError;

  if (!err || err.wasNotified || err.preventNotify) {
    return;
  }

  let notifyMessage = customMessage || getErrorMessage(err);

  if (err.isAxiosError) {
    notifyMessage = getAxiosErrorMessage(err as AxiosError, { customMessage });
  }

  notify.error(notifyMessage);

  if (isObject(err)) {
    // if it can have its own properties and is not null
    err.wasNotified = true;
  }
}

/**
 * Sets the preventNotify property to true (if able), which prevents notifyError from creating a notification
 * @param {unknown} error - should be an Error object
 */
export function preventErrorNotification(error: unknown) {
  if (isObject(error)) {
    (error as APIError).preventNotify = true;
  }
}

/**
 * A wrapper around console.error to ensure implementation details are not logged in production
 * @param {unknown} error - should be an Error object
 */
export function logError(error?: unknown) {
  const err = error as APIError;

  // the browser automatically logs http errors, so skip logging Axios errors
  if (err && (err.isAxiosError || err.wasLogged)) {
    return;
  }

  if (env.MODE === 'production') {
    if (!err || !err.message) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('%cError:', 'color: red; font-weight: bold', err.message);
  } else {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  if (isObject(err)) {
    err.wasLogged = true;
  }
}

/**
 * Get a client-facing message from an axios (http) error
 * @param {AxiosError} error - an axios error instance
 * @param {object} [options] - extra arguments
 * @param {string} [options.customMessage] - A context-specific message for the error.
 */
export function getAxiosErrorMessage(
  error: AxiosError,
  options?: {
    customMessage?: string;
  },
): string {
  const { request, response } = error;
  const { customMessage = '' } = options || {};
  const title = customMessage ? String(customMessage) : getErrorMessage(error);
  const jsonApiErrors = getJsonApiErrors(error);

  if (
    !response ||
    isEmpty(response.data) ||
    response.status === 500 ||
    jsonApiErrors.find((e) => String(e.status) === '500') ||
    typeof response.data === 'string' // Django html responses
  ) {
    return `${title}\n\n${CUSTOMER_SUPPORT_MESSAGE}`;
  }

  if (jsonApiErrors.length && request.responseURL?.includes(URLS.CUSTOM_ERROR)) {
    const errorList = jsonApiErrors.map((e) => {
      if (!e.source?.pointer || e.source?.pointer === '/non_field_errors') {
        return e.detail;
      }
      return `${e.source?.pointer.replace(/^\//, '')}: ${e.detail}`;
    });

    return `${title}\n\n${errorList.join('\n\n')}`;
  }

  if (typeof response.data === 'object' && response.data !== null) {
    return `${title}\n\n${Object.values(response.data).join('\n\n')}`;
  }

  return `${title}\n\n${String(response.data)}`;
}

export function isJsonError(error: unknown): error is JsonError {
  return typeof error === 'object' && error !== null && 'detail' in error;
}

export function isJsonErrorDocument(document: unknown): document is JsonErrorDocument {
  return (
    typeof document === 'object' &&
    document !== null &&
    'errors' in document &&
    Array.isArray(document.errors) &&
    document?.errors.every(isJsonError)
  );
}

/**
 * Attempts to get a list of JSON:API errors from an error instance
 * @param {unknown} error - should be an Error object
 * @returns {JsonError[]} a list of errors in the JSON:API error format
 * @see https://jsonapi.org/examples/#error-objects
 */
export function getJsonApiErrors(error: unknown): JsonError[] {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('response' in error) ||
    typeof error.response !== 'object' ||
    error.response === null ||
    !('data' in error.response) ||
    typeof error.response.data !== 'object' ||
    !isJsonErrorDocument(error.response.data)
  ) {
    return [];
  }

  return error.response.data.errors.map((e) => {
    return {
      ...e,
      source: {
        ...e.source,
        // Every JSON:API error is supposed to have a source object with a pointer field, but we still
        // have some errors that don't follow this spec; therefore, source and source.pointer is not guaranteed.
        pointer: String(e.source?.pointer || e.source || e.title),
      },
    };
  });
}

export function isHttpError(error?: unknown): error is AxiosError {
  return !!(error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError);
}

type ErrorWithMessage = {
  message: string;
};

/**
 * Does a given error have a message property?
 * @param {unknown} error - should be an Error object
 * @returns {boolean} does the error have a message property?
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && 'message' in error;
}

/**
 * Get a readable message from an error
 * @param {unknown} error - should be an Error object
 * @returns {string} the error's message
 */
export function getErrorMessage(error: unknown): string {
  if (isErrorWithMessage(error)) {
    return error.message;
  }

  if (isPlainObject(error) || Array.isArray(error)) {
    try {
      return JSON.stringify(error) || ''; // ex: {error: 'Invalid', source: 'foo'}
    } catch {
      // fallback when stringifying the error fails, i.e. when error contains circular references
      return String(error);
    }
  } else {
    return String(error);
  }
}

/**
 * Parse JSON API errors
 * @param {JsonError[]} errors - list of errors
 * @returns {object} Error details keyed by field
 */
export function groupErrorsByField(errors: JsonError[]): { [key: string]: string } {
  return errors.reduce((acc, error) => {
    const { pointer = '' } = error.source || {};
    const [, field] = pointer.split('/');

    /**
     * NOTE: there is/was an issue with some JSONApi errors having a null source object.
     * Ideally, the source object would have a pointer key and not be null.
     * If it doesn't (or if the pointer is malformed and doesn't yield a `field`),
     * we shouldn't try to group by a non-existent field.
     */
    if (field) {
      if (!acc[field]) {
        acc[field] = [];
      }

      acc[field].push(error.detail);
    }

    return acc;
  }, {});
}

/**
 * Initializes global error handlers that ensure all uncaught errors are
 * notified to the user and logged for the developer.
 *
 * To prevent notifying the user while allowing it to be logged and reported:
 *    1. catch an error
 *    2. do `error.preventNotify = true` (preventNotify is handled within notifyError)
 *    3. throw/reject the error (so that Sentry can report it); alternatively, you can
 *       do `window.Sentry?.captureException(error); logError(error);`
 */
export function initGlobalErrorHandlers(app: App) {
  if ((initGlobalErrorHandlers as ((app: App) => void) & { isInitialized: boolean }).isInitialized) {
    return;
  }

  (initGlobalErrorHandlers as ((app: App) => void) & { isInitialized: boolean }).isInitialized = true;

  const oldVueErrorHandler = app.config.errorHandler;
  const oldOnError = window.onerror;
  const oldOnUnhandledRejection = window.onunhandledrejection;

  app.config.errorHandler = (error, instance, info) => {
    logError(error);

    if (isHttpError(error) || error instanceof OmsServiceError) {
      notifyError(error);
    }

    if (oldVueErrorHandler) {
      // necessary because Sentry uses app.config.errorHandler too
      oldVueErrorHandler.apply(window, [error, instance, info]);
    }
  };

  window.onerror = function (...args) {
    const error = args[4];

    logError(error);

    if (isHttpError(error) || error instanceof OmsServiceError) {
      notifyError(error);
    }

    if (oldOnError) {
      oldOnError.apply(window, args);
    }
  };

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const error = event?.reason;

    if (error) {
      event.preventDefault();

      logError(error);

      if (isHttpError(error) || error instanceof OmsServiceError) {
        notifyError(error);
      }
    }

    if (oldOnUnhandledRejection) {
      oldOnUnhandledRejection.apply(window, [event]);
    }
  };
}

export type APAPIErrorList = Record<string, string[] | Record<string, string[]>[] | Array<Array<string>>>;

// when order already exists the number will be returned as string instead of an array
const mapErrorListToString = (apiError: string | string[] | Array<Array<string>>, field: string, lineIndex: number) => {
  const result =
    typeof apiError === 'string'
      ? `<b>${field}</b>: ${apiError}`
      : Object.keys(apiError || {}).map((key) => {
          return `<b>${key}</b> at line <b>${lineIndex + 1}</b>: ${
            Array.isArray(apiError[key]) ? apiError[key]?.join(`,\n`) : apiError[field]
          }`;
        });

  return result;
};

const mapErrorArrayToString = (apiError: Array<Array<string>>, field: string) => {
  const result = apiError.map((errors) => {
    return errors.length > 0 ? `<b>${field}</b>: ${errors}\n` : '';
  });

  return result;
};

/**
 * @deprecated TODO: update the backend endpoints which use this error format to instead use the JSON:API error format. See: https://jsonapi.org/examples/#error-objects
 *
 * Parse JSON API errors for offline orders csv upload
 *
 * 	API returns an error based on the `APAPIErrorList[]` structure.
 *		E.G: [{ line_items: [{ quantity: ['A valid number is required.'] }, {}] }];
 *		OR: [{ number: ['Order number already exists.'] }];
 *		OR: [{ orders: [[], ['"1sa2err3" is not a valid UUID.'], ['"43423dew" is not a valid UUID.']] }];
 */
export const getApiErrorListMessage = (errors: APAPIErrorList[]) => {
  if (!Array.isArray(errors)) {
    return '';
  }

  return errors
    .map((error) =>
      Object.keys(error || {})
        .map((key) =>
          error[key]
            .map((apiError, index) =>
              Array.isArray(apiError)
                ? mapErrorArrayToString(apiError, key)
                : mapErrorListToString(apiError, key, index),
            )
            .join(`\n`),
        )
        .join(`\n\n`),
    )
    .join('\n');
};
